import {
  createCustomerRequestSchema,
  createInvoiceRequestSchema,
  recordPaymentRequestSchema,
  updateCustomerRequestSchema,
  updateInvoiceRequestSchema,
  issueReceiptRequestSchema,
  updateSellerProfileRequestSchema,
  type CreateCustomerRequest,
  type CreateInvoiceRequest,
  type Customer,
  type InvoiceDetail,
  type InvoiceUsage,
  type Receipt,
  type RecordPaymentRequest,
  type SellerProfile,
  type UpdateCustomerRequest,
  type UpdateInvoiceRequest,
  type UpdateSellerProfileRequest,
} from "@stb/api-contract"
import { createServerFn } from "@tanstack/react-start"
import { z } from "zod"

import { requireFeature } from "@/billing/entitlements.server"
import { isAppError } from "@/core/app-error"

import {
  createCustomer,
  deleteCustomer,
  getCustomer,
  listCustomers,
  updateCustomer,
} from "../customers.server"
import { buildInvoiceDocument, type InvoiceDocumentData } from "../document"
import { toDocumentInput } from "../formatters.server"
import {
  createInvoice,
  deleteDraftInvoice,
  getInvoice,
  listInvoices,
  loadInvoiceParts,
  updateInvoice,
  voidInvoice,
} from "../invoices.server"
import { deletePayment, recordPayment } from "../payments.server"
import type { ReceiptDocumentData } from "../receipt-document"
import {
  getReceiptDocument,
  getReceiptPdfUrl,
  issueReceipt,
  listReceipts,
  sendReceipt,
  type SendReceiptResult,
} from "../receipts.server"
import { getSellerProfile, loadOrganisation, updateSellerProfile } from "../seller-profile.server"
import { createInvoiceSendDeps } from "../send-deps.server"
import {
  processInvoiceSend,
  requestInvoiceSend,
  undoInvoiceSend,
  type InvoiceSendTicket,
  type ProcessSendResult,
} from "../send.server"
import {
  INVOICE_PDF_DOWNLOAD_TTL_SECONDS,
  getInvoicePdfStorage,
  getLogoStorage,
} from "../storage.server"
import { getInvoiceUsage } from "../usage.server"
import { requireInvoicingContext, runAction, type RpcResult } from "./context"

/**
 * Server functions for the invoicing UI (ADR-012: web only, `rpc/` only). Reads return data and
 * throw a plain Error; they are never feature-guarded, so history stays visible after a
 * downgrade. Writes return `RpcResult` and go through a service function that calls
 * `requireFeature("invoicing")`, except the business profile (`updateSellerProfile`, T1402): its
 * fields also head receipts and the logbook report, so every plan may edit them. The organisation comes from the session, never from the input.
 */

const idSchema = z.object({ id: z.uuid() })

/** Reads: AppError becomes a plain Error with the message (NOT_FOUND etc.). */
async function read<T>(
  run: (scope: Awaited<ReturnType<typeof requireInvoicingContext>>) => Promise<T>,
) {
  const scope = await requireInvoicingContext()
  try {
    return await run(scope)
  } catch (error) {
    if (isAppError(error)) throw new Error(error.message, { cause: error })
    throw error
  }
}

// --- Reads ------------------------------------------------------------------------------------

/** Invoices, newest first, without lines; each has `display_status`, `paid_minor`, `customer`. */
export const loadInvoiceList = createServerFn({ method: "GET" })
  .validator((data?: { customerId?: string }) =>
    z.object({ customerId: z.uuid().optional() }).parse(data ?? {}),
  )
  .handler(async ({ data }): Promise<InvoiceDetail[]> =>
    read(({ context }) => listInvoices(context, { customerId: data?.customerId })),
  )

export const loadInvoice = createServerFn({ method: "GET" })
  .validator((data: { id: string }) => idSchema.parse(data))
  .handler(async ({ data }): Promise<InvoiceDetail> =>
    read(({ context }) => getInvoice(data.id, context)),
  )

/** Display data for the preview page (the same data the PDF and email are built from). */
export const loadInvoiceDocument = createServerFn({ method: "GET" })
  .validator((data: { id: string }) => idSchema.parse(data))
  .handler(async ({ data }): Promise<InvoiceDocumentData> =>
    read(async ({ context }) => {
      const parts = await loadInvoiceParts(context, data.id)
      const seller = await loadOrganisation(context)
      const input = toDocumentInput(
        parts.invoice,
        parts.lines,
        seller,
        {
          // the stored snapshot: a later rename or ABN change must not rewrite an issued invoice
          name: parts.invoice.buyerName,
          abn: parts.invoice.buyerAbn,
          address: parts.customer?.address ?? null,
        },
        parts.payments,
      )
      return buildInvoiceDocument(input)
    }),
  )

/** Short-lived signed URL of a sent invoice's stored PDF; null while there is none. */
export const loadInvoicePdfUrl = createServerFn({ method: "GET" })
  .validator((data: { id: string }) => idSchema.parse(data))
  .handler(async ({ data }): Promise<string | null> =>
    read(async ({ context }) => {
      const { invoice } = await loadInvoiceParts(context, data.id)
      if (!invoice.pdfStoragePath) return null
      return getInvoicePdfStorage().createDownloadUrl(
        invoice.pdfStoragePath,
        INVOICE_PDF_DOWNLOAD_TTL_SECONDS,
      )
    }),
  )

export const loadInvoiceUsage = createServerFn({ method: "GET" }).handler(
  async (): Promise<InvoiceUsage> => read(({ context }) => getInvoiceUsage(context)),
)

export const loadSellerProfile = createServerFn({ method: "GET" }).handler(
  async (): Promise<SellerProfile> => read(({ context }) => getSellerProfile(context)),
)

export const loadCustomers = createServerFn({ method: "GET" }).handler(
  async (): Promise<Customer[]> => read(({ context }) => listCustomers(context)),
)

export const loadCustomer = createServerFn({ method: "GET" })
  .validator((data: { id: string }) => idSchema.parse(data))
  .handler(async ({ data }): Promise<Customer> =>
    read(({ context }) => getCustomer(data.id, context)),
  )

// --- Invoice writes ---------------------------------------------------------------------------

export const createInvoiceAction = createServerFn({ method: "POST" })
  .validator((data: CreateInvoiceRequest) => createInvoiceRequestSchema.parse(data))
  .handler(async ({ data }): Promise<RpcResult<InvoiceDetail>> =>
    runAction(({ context }) => createInvoice(data, context)),
  )

export const updateInvoiceAction = createServerFn({ method: "POST" })
  .validator((data: { id: string; request: UpdateInvoiceRequest }) => ({
    id: z.uuid().parse(data.id),
    request: updateInvoiceRequestSchema.parse(data.request),
  }))
  .handler(async ({ data }): Promise<RpcResult<InvoiceDetail>> =>
    runAction(({ context }) => updateInvoice(data.id, data.request, context)),
  )

export const deleteDraftInvoiceAction = createServerFn({ method: "POST" })
  .validator((data: { id: string }) => idSchema.parse(data))
  .handler(async ({ data }): Promise<RpcResult<null>> =>
    runAction(async ({ context }) => {
      await deleteDraftInvoice(data.id, context)
      return null
    }),
  )

export const voidInvoiceAction = createServerFn({ method: "POST" })
  .validator((data: { id: string }) => idSchema.parse(data))
  .handler(async ({ data }): Promise<RpcResult<InvoiceDetail>> =>
    runAction(({ context }) => voidInvoice(data.id, context)),
  )

// --- Send: request -> (undo window) -> process ------------------------------------------------

/**
 * Validates, numbers and queues a send. The UI shows "Sending..." with Undo for
 * `undoWindowSeconds`, then calls `processInvoiceSendAction` with the returned token.
 */
export const sendInvoiceAction = createServerFn({ method: "POST" })
  .validator((data: { id: string; toEmail?: string | null }) => ({
    id: z.uuid().parse(data.id),
    toEmail: z.email().max(254).nullish().parse(data.toEmail),
  }))
  .handler(async ({ data }): Promise<RpcResult<InvoiceSendTicket>> =>
    runAction(({ context }) =>
      requestInvoiceSend(
        { invoiceId: data.id, toEmail: data.toEmail },
        context,
        new Date(),
        getLogoStorage(),
      ),
    ),
  )

export const undoInvoiceSendAction = createServerFn({ method: "POST" })
  .validator((data: { id: string; sendToken: string }) => ({
    id: z.uuid().parse(data.id),
    sendToken: z.uuid().parse(data.sendToken),
  }))
  .handler(async ({ data }): Promise<RpcResult<InvoiceDetail>> =>
    runAction(({ context }) =>
      undoInvoiceSend({ invoiceId: data.id, sendToken: data.sendToken }, context),
    ),
  )

/**
 * Runs the queued send (PDF, storage, email) and returns its outcome. The Cloudflare Queue
 * consumer is not wired yet, so the UI calls this when the undo window ends; once the queue
 * exists the same `processInvoiceSend` runs from the consumer instead. "Try again" after
 * `outcome: "failed"` is `sendInvoiceAction` again.
 */
export const processInvoiceSendAction = createServerFn({ method: "POST" })
  .validator((data: { id: string; sendToken: string }) => ({
    id: z.uuid().parse(data.id),
    sendToken: z.uuid().parse(data.sendToken),
  }))
  .handler(
    async ({ data }): Promise<RpcResult<{ result: ProcessSendResult; invoice: InvoiceDetail }>> =>
      runAction(async ({ user, context }) => {
        requireFeature(context.features, "invoicing")
        const result = await processInvoiceSend(
          { invoiceId: data.id, sendToken: data.sendToken, replyTo: user.email },
          context,
          createInvoiceSendDeps(),
        )
        return { result, invoice: await getInvoice(data.id, context) }
      }),
  )

// --- Payments ---------------------------------------------------------------------------------

export const recordPaymentAction = createServerFn({ method: "POST" })
  .validator((data: { invoiceId: string; request: RecordPaymentRequest }) => ({
    invoiceId: z.uuid().parse(data.invoiceId),
    request: recordPaymentRequestSchema.parse(data.request),
  }))
  .handler(async ({ data }): Promise<RpcResult<InvoiceDetail>> =>
    runAction(({ context }) => recordPayment(data.invoiceId, data.request, context)),
  )

export const deletePaymentAction = createServerFn({ method: "POST" })
  .validator((data: { paymentId: string }) => ({ paymentId: z.uuid().parse(data.paymentId) }))
  .handler(async ({ data }): Promise<RpcResult<InvoiceDetail>> =>
    runAction(({ context }) => deletePayment(data.paymentId, context)),
  )

// --- Payment receipts -------------------------------------------------------------------------

export const loadReceipts = createServerFn({ method: "GET" })
  .validator((data: { invoiceId: string }) => ({ invoiceId: z.uuid().parse(data.invoiceId) }))
  .handler(async ({ data }): Promise<Receipt[]> =>
    read(({ context }) => listReceipts(data.invoiceId, context)),
  )

export const loadReceiptDocument = createServerFn({ method: "GET" })
  .validator((data: { id: string }) => idSchema.parse(data))
  .handler(async ({ data }): Promise<ReceiptDocumentData> =>
    read(({ context }) => getReceiptDocument(data.id, context)),
  )

/** Short-lived signed URL of the stored receipt PDF; null while the PDF is missing (issue again). */
export const loadReceiptPdfUrl = createServerFn({ method: "GET" })
  .validator((data: { id: string }) => idSchema.parse(data))
  .handler(async ({ data }): Promise<string | null> =>
    read(({ context }) => getReceiptPdfUrl(data.id, context, getInvoicePdfStorage())),
  )

/** Issues (or returns) the receipt of a payment. Free plan too; not counted in the invoice quota. */
export const issueReceiptAction = createServerFn({ method: "POST" })
  .validator((data: { paymentId: string }) =>
    issueReceiptRequestSchema.parse({ payment_id: data.paymentId }),
  )
  .handler(async ({ data }): Promise<RpcResult<Receipt>> =>
    runAction(({ context }) => issueReceipt(data.payment_id, context, createInvoiceSendDeps())),
  )

export const sendReceiptAction = createServerFn({ method: "POST" })
  .validator((data: { id: string; toEmail?: string | null }) => ({
    id: z.uuid().parse(data.id),
    toEmail: z.email().max(254).nullish().parse(data.toEmail),
  }))
  .handler(async ({ data }): Promise<RpcResult<SendReceiptResult>> =>
    runAction(({ user, context }) =>
      sendReceipt(
        { receiptId: data.id, toEmail: data.toEmail, replyTo: user.email },
        context,
        createInvoiceSendDeps(),
      ),
    ),
  )

// --- Customers and seller profile -------------------------------------------------------------

export const createCustomerAction = createServerFn({ method: "POST" })
  .validator((data: CreateCustomerRequest) => createCustomerRequestSchema.parse(data))
  .handler(async ({ data }): Promise<RpcResult<Customer>> =>
    runAction(({ context }) => createCustomer(data, context)),
  )

export const updateCustomerAction = createServerFn({ method: "POST" })
  .validator((data: { id: string; request: UpdateCustomerRequest }) => ({
    id: z.uuid().parse(data.id),
    request: updateCustomerRequestSchema.parse(data.request),
  }))
  .handler(async ({ data }): Promise<RpcResult<Customer>> =>
    runAction(({ context }) => updateCustomer(data.id, data.request, context)),
  )

export const deleteCustomerAction = createServerFn({ method: "POST" })
  .validator((data: { id: string }) => idSchema.parse(data))
  .handler(async ({ data }): Promise<RpcResult<null>> =>
    runAction(async ({ context }) => {
      await deleteCustomer(data.id, context)
      return null
    }),
  )

export const updateSellerProfileAction = createServerFn({ method: "POST" })
  .validator((data: UpdateSellerProfileRequest) => updateSellerProfileRequestSchema.parse(data))
  .handler(async ({ data }): Promise<RpcResult<SellerProfile>> =>
    runAction(({ context }) => updateSellerProfile(data, context)),
  )
